import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";

const mapicCannes: ChauffeurPage = {
  slug: "mapic-cannes",
  title: "Chauffeur privé MAPIC Cannes – VTC professionnel",
  subtitle:
    "Chauffeur privé pendant le MAPIC à Cannes : navettes B2B, accueil aéroport de Nice et mises à disposition pour délégations autour du Palais des Festivals.",
  keywords: [
    "chauffeur mapic cannes",
    "vtc mapic",
    "navette mapic palais des festivals",
  ],
  hero: {
    title: "Chauffeur privé pendant le MAPIC à Cannes",
    subtitle:
      "Salon international de l'immobilier commercial en novembre : un chauffeur dédié pour vos rendez-vous B2B autour du Palais des Festivals.",
    keywords: [
      "chauffeur MAPIC",
      "VTC salon Cannes",
      "navette Palais des Festivals",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé pendant le MAPIC à Cannes",
    badges: ["Accueil aéroport VIP", "Navettes B2B", "Mise à disposition"],
  },
  benefits: {
    heading: "Pourquoi un chauffeur pendant le MAPIC",
    items: [
      {
        title: "Logistique de salon B2B",
        description:
          "Rendez-vous serrés, stands et dîners d'affaires : le chauffeur enchaîne vos trajets pour tenir un agenda professionnel dense.",
      },
      {
        title: "Accueil aéroport de Nice",
        description:
          "Beaucoup de délégations arrivent la veille. Accueil pancarte, suivi de vol et transfert direct vers votre hôtel cannois.",
      },
      {
        title: "Palais des Festivals à pied d'œuvre",
        description:
          "Dépose au plus près des entrées du Palais et des halls d'exposition, selon l'affluence du salon.",
      },
      {
        title: "Navettes équipes & partenaires",
        description:
          "Coordination multi-véhicules pour exposants, clients et partenaires entre hôtels, Palais et restaurants.",
      },
    ],
  },
  services: {
    heading: "Services chauffeur pendant le MAPIC",
    services: [
      {
        title: "Accueil aéroport de Nice",
        text: "Transfert Nice ↔ Cannes avec suivi de vol pendant les journées du MAPIC.",
      },
      {
        title: "Navettes hôtels ↔ Palais",
        text: "Rotations entre vos hôtels, le Palais des Festivals et les lieux de rendez-vous.",
      },
      {
        title: "Mise à disposition journée",
        text: "Chauffeur dédié à l'heure ou à la journée pour un agenda B2B chargé.",
      },
      {
        title: "Dîners d'affaires & soirées",
        text: "Restaurants de la Croisette et de l'arrière-pays, retour de nuit assuré.",
      },
      {
        title: "Transferts inter-villes",
        text: "Liaisons vers Monaco, Nice ou Antibes pour rendez-vous et extensions de séjour.",
      },
    ],
  },
  locations: {
    heading: "Points clés pendant le MAPIC",
    items: [
      {
        name: "Palais des Festivals et des Congrès",
        description: "Halls d'exposition et espaces de conférences du salon.",
      },
      {
        name: "La Croisette",
        description:
          "Hôtels d'affaires (Carlton, Martinez, Majestic) et lieux de networking.",
      },
      {
        name: "Aéroport de Nice Côte d'Azur",
        description: "Terminaux 1 et 2, principales arrivées des délégations.",
      },
      {
        name: "Quartier des affaires & hôtels",
        description:
          "Établissements accueillant exposants et visiteurs professionnels.",
      },
      {
        name: "Mougins & arrière-pays",
        description: "Dîners d'affaires et soirées hors du centre.",
      },
    ],
  },
  generic: {
    heading: "Le MAPIC vu d'un chauffeur",
    subheading:
      "En novembre, le Palais des Festivals accueille le salon international de l'immobilier commercial et un public strictement professionnel.",
    paragraphs: [
      "Chaque mois de novembre, le MAPIC réunit à Cannes promoteurs, enseignes et investisseurs du commerce et de l'immobilier commercial au Palais des Festivals. Les journées enchaînent rendez-vous, stands et dîners : un chauffeur dédié fait gagner un temps précieux entre l'hôtel, le Palais et les lieux de networking.",
      "Les délégations arrivent surtout par l'aéroport de Nice Côte d'Azur. Avec le suivi de vol et l'accueil pancarte, nous absorbons les décalages et déposons vos équipes directement à l'hôtel, prêtes pour le premier rendez-vous.",
      "Le salon est une affaire d'agenda : la mise à disposition garde le même chauffeur sur la journée, coordonne plusieurs véhicules pour vos partenaires et assure les retours après les dîners d'affaires.",
    ],
    bullets: [
      "Navettes hôtels ↔ Palais",
      "Suivi de vol en novembre",
      "Coordination multi-véhicules B2B",
    ],
  },
  process: {
    heading: "Réserver un chauffeur pour le MAPIC",
    steps: [
      "Indiquez vos dates, vos hôtels et votre agenda de rendez-vous professionnels.",
      "Nous organisons les navettes entre hôtels, Palais des Festivals et lieux de networking.",
      "Le chauffeur se présente en tenue, avec suivi de vol pour vos arrivées à Nice.",
      "Mise à disposition ou coordination multi-véhicules pour toute la délégation.",
    ],
  },
  faq: [
    {
      q: "Comment réserver un chauffeur pour le MAPIC à Cannes ?",
      a: "Réservez à l'avance : le MAPIC concentre en novembre une clientèle B2B nombreuse. Précisez vos dates, vos hôtels et votre agenda pour que nous calions un chauffeur dédié ou plusieurs véhicules pour votre délégation.",
    },
    {
      q: "Assurez-vous des navettes entre l'hôtel et le Palais des Festivals ?",
      a: "Oui. Nous organisons des rotations entre vos hôtels, le Palais des Festivals et vos lieux de rendez-vous, pour tenir un agenda de salon serré sans perte de temps.",
    },
    {
      q: "Gérez-vous les arrivées à l'aéroport de Nice ?",
      a: "Oui, avec suivi de vol et accueil pancarte. La plupart des délégations arrivent par Nice Côte d'Azur ; nous adaptons les prises en charge aux horaires et aux retards.",
    },
    {
      q: "Pouvez-vous coordonner plusieurs véhicules pour une délégation ?",
      a: "Oui. Pour les exposants et les équipes, nous coordonnons plusieurs véhicules entre hôtels, Palais et restaurants, avec un interlocuteur unique.",
    },
    {
      q: "Proposez-vous une mise à disposition pour les dîners d'affaires ?",
      a: "Oui. Le chauffeur reste disponible en soirée pour vos dîners sur la Croisette ou dans l'arrière-pays et assure le retour de nuit.",
    },
    {
      q: "Pourquoi choisir KS Rent Services pour le MAPIC ?",
      a: "Notre service de chauffeur privé sur la Côte d'Azur est noté 5,0/5 sur 38 avis Google : ponctualité, discrétion et connaissance du Palais des Festivals, essentielles pour une clientèle professionnelle.",
    },
  ],
  relatedLinks: [
    { label: "Chauffeur privé à Cannes", href: "/chauffeur/cannes" },
    { label: "Chauffeur d'entreprise", href: "/chauffeur/entreprise" },
    { label: "Réserver un transfert aéroport", href: "/chauffeur/aeroport" },
    {
      label: "Location avec livraison à Cannes",
      href: "/location/location-voiture-cannes",
    },
  ],
  cta: {
    title: "Votre chauffeur pour le MAPIC à Cannes",
    text: "Navettes B2B, accueil aéroport et dîners d'affaires : réservez un chauffeur dédié pour le salon.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver un chauffeur",
  },
  seo: {
    title: "Chauffeur MAPIC Cannes — VTC salon & navettes pro",
    description:
      "Chauffeur privé pendant le MAPIC à Cannes : navettes B2B entre hôtels et Palais des Festivals, accueil aéroport de Nice et mise à disposition. Réservez.",
    canonical: "/chauffeur/mapic-cannes",
  },
};

export default mapicCannes;
